import React, { useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from '@material-ui/lab/Autocomplete';

import ActionButton from '../button/actionButton';
import { InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import TextField from '../input/textField';
import SuccessTick from '../success/successTick';
import CircularLoader from '../loading/circularLoader';
import GenericError from '../error/genericError';
import { useTransactionFormControls } from '../../utils/formControls/transactionFormControls';
import ModalConsumer from "../consumer/modalConsumer";
import SendFiatRecipientDetailsDialog from "./SendFiatRecipientDetailsDialog";
import SendStablecoinRecipientDetailsDialog from "./SendStablecoinRecipientDetailsDialog";
import { DEPOSIT_CURRENCIES, TRANSACTION_TYPES } from '../../utils/types';
import toast from 'react-simple-toasts';
import { client } from "../../utils/data/graphql/client";
import { createOtcOrderAdminMutation } from "../../utils/data/graphql/otc/mutations";
import { useAuth0 } from "@auth0/auth0-react";

const useStyles = makeStyles((theme) => ({
    root: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: '416px',
        height: '706px',
        borderRadius: '20px',
        background: theme.background.dialog
    },
    cardContent: {
        height: '90%',
        margin: '32px !important',
        padding: '0 !important',
        display: 'flex',
        justifyContent: 'center',
    },
    error: {
        marginTop: '60%'
    },
    success: {
        height: '95%',
        marginLeft: '24px'
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(3),
    },
    backdrop: {
        ['@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))']: {
            '-webkit-backdrop-filter': 'blur(2em)',
            backdropFilter: 'blur(2em)',
            backgroundColor: 'rgb(0 0 0 / 33%) !important',
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '42px',
    },
    confirmButton: {
        marginBottom: '40px',
        position: 'absolute',
        bottom: 0,
    },
    heading: {
        marginTop: '12px',
        alignSelf: 'start'
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px 0px 32px 0px'
    },
    input: {
        margin: '32px 0px 12px 0px !important',
    },
    selectLabel: {
        marginBottom: '4px'
    },
    autocompleteOption: {
        color: '#000000 !important',
        backgroundColor: '#ffffff !important',
        '&[data-focus="true"]': {
            backgroundColor: '#f5f5f5 !important',
        },
        '&[aria-selected="true"]': {
            backgroundColor: '#e0e0e0 !important',
        },
    },
    autocompleteListbox: {
        backgroundColor: '#ffffff !important',
        '& .MuiAutocomplete-option': {
            color: '#000000 !important',
        },
    },
    resultContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignContent: 'center',
        height: '100%',
    },
}));

const PendingTransactionDialog = ({ onClose, onComplete, transactionType, transactionCategory, accountId, customerId, selectableCurrencies }) => {
    const classes = useStyles();
    const { user } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [success, setSuccess] = useState(false);
    const [submitRequested, setSubmitRequested] = useState(false);
    const [depositType, setDepositType] = useState('BANK'); // BANK or STABLECOIN
    
    // Determine if this is a Send transaction
    const isSend = transactionType === TRANSACTION_TYPES.SEND_FIAT;
    
    // Define stablecoins to exclude from bank deposits
    const STABLECOINS = ['USDC', 'USDT', 'BUSD'];
    
    // Determine currencies based on deposit type (for Top up only)
    const isTopUp = transactionCategory === 'Top up';
    const currencies = React.useMemo(() => {
        if (isTopUp) {
            if (depositType === 'STABLECOIN') {
                return ['USDT', 'USDC'];
            } else {
                // Bank deposits - prioritize popular currencies
                const allCurrencies = Object.keys(DEPOSIT_CURRENCIES || {}).filter(c => !STABLECOINS.includes(c)).sort();
                const popularCurrencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'HKD', 'JPY'];
                const popular = allCurrencies.filter(c => popularCurrencies.includes(c));
                const others = allCurrencies.filter(c => !popularCurrencies.includes(c));
                return [...popular, ...others];
            }
        } else {
            return selectableCurrencies === undefined || selectableCurrencies.length === 0 ? ["AUD", "USD"] : [...selectableCurrencies];
        }
    }, [isTopUp, depositType, selectableCurrencies]);
    
    const {
        getValues,
        handleTextInputValue,
        handleCreateTransactionFormSubmit,
        formIsValid,
        inputErrors
    } = useTransactionFormControls({
        accountId,
        customerId,
        currency: transactionType === TRANSACTION_TYPES.SEND_FIAT ? 'USD' : undefined,
    });

    const currentValues = getValues();

    const submitCallback = (success) => {
        setLoading(false);
        if (success) {
            setSuccess(true);
            setError(false);
            onComplete();
        } else {
            setError(true)
        }
    }

    useEffect(() => {
        if (loading && submitRequested) {
            handleCreateTransactionFormSubmit(submitCallback, transactionType);
        }
    }, [submitRequested]);

    return (
        <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
            <Fade in={true}>
                <Card className={classes.root}>
                    <CardContent className={classes.cardContent}>
                        <ActionButton variant="secondary" size="lg"
                            className={classes.closeButton}
                            onClick={onClose}
                        >
                            Close
                        </ActionButton>
                        {
                            error ? <div className={classes.resultContainer}>
                                        <GenericError onClick={() => { setError(false); setLoading(true); handleCreateTransactionFormSubmit(submitCallback, transactionType);}}/>
                                    </div> :
                            success ? <div className={classes.resultContainer}><SuccessTick trigger={success} tickSize='175px'/></div> :
                            loading ? <div className={classes.resultContainer}><CircularLoader trigger={loading} loaderSize='125px'/></div> :
                            <div className={classes.content}>
                                <Typography variant="h3" className={classes.heading}>{`Create a pending ${transactionCategory?.toLowerCase()} transaction`}</Typography>
                                <Typography variant="body1" style={{ alignSelf: 'start', marginTop: '8px' }}>
                                    Enter the transaction details below.
                                </Typography>
                                <div className={classes.inputContainer}>
                                    {(isTopUp || isSend) && (
                                        <>
                                            <InputLabel htmlFor='deposit-type-select' className={classes.selectLabel}>
                                                {isSend ? 'Payout method' : 'Deposit type'}
                                            </InputLabel>
                                            <Select
                                                className={classes.select}
                                                id='deposit-type-select'
                                                label={isSend ? 'Payout method' : 'Deposit type'}
                                                name='depositType'
                                                value={depositType}
                                                onChange={(e) => {
                                                    setDepositType(e.target.value);
                                                    // Reset currency when switching deposit types (for Top up only)
                                                    if (isTopUp) {
                                                        handleTextInputValue({ target: { name: 'currency', value: e.target.value === 'BANK' ? 'USD' : 'USDT' } });
                                                    }
                                                }}
                                            >
                                                <MenuItem value={'BANK'}>Bank</MenuItem>
                                                <MenuItem value={'STABLECOIN'}>Stablecoin</MenuItem>
                                            </Select>
                                        </>
                                    )}
                                    {/* Date field for Send flow */}
                                    {isSend && (
                                        <TextField
                                            className={classes.input}
                                            name='date'
                                            type='date'
                                            value={currentValues.date ?? new Date().toISOString().substring(0,10)}
                                            label={'Date'}
                                            InputLabelProps={{shrink: true, required: false, disableAnimation: true}}
                                            onChange={handleTextInputValue}
                                        />
                                    )}
                                    {/* For non-Send flows, show currency and amount */}
                                    {!isSend && (
                                        <>
                                            {/* Date field for Deposits */}
                                            <TextField
                                                className={classes.input}
                                                name='date'
                                                type='date'
                                                value={currentValues.date ?? new Date().toISOString().substring(0,10)}
                                                label={'Transaction Date'}
                                                InputLabelProps={{shrink: true, required: false, disableAnimation: true}}
                                                onChange={handleTextInputValue}
                                            />
                                        
                                            <Autocomplete
                                                className={classes.input}
                                                style={{paddingLeft: '0px'}}
                                                options={currencies}
                                                value={currentValues['currency'] || 'USD'}
                                                onChange={(event, newValue) => {
                                                    handleTextInputValue({ target: { name: 'currency', value: newValue || 'USD' } });
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Currency"
                                                        InputLabelProps={{shrink: false, disableAnimation: true}}
                                                        placeholder="Type to search..."
                                                        style={{paddingLeft: '16px'}}
                                                    />
                                                )}
                                                freeSolo={false}
                                                disableClearable
                                                classes={{
                                                    option: classes.autocompleteOption,
                                                    listbox: classes.autocompleteListbox,
                                                }}
                                                ListboxProps={{
                                                    style: {
                                                        backgroundColor: '#ffffff',
                                                        color: '#000000',
                                                    }
                                                }}
                                            />
                                            <TextField
                                                className={classes.input}
                                                name='depositAmount'
                                                type='text'
                                                value={currentValues.depositAmount}
                                                label={`Amount ${currentValues?.currency}`}
                                                InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                                onChange={handleTextInputValue}
                                                {...(inputErrors['depositAmount'] && { error: true, helperText: inputErrors['depositAmount'] })}
                                            />
                                        </>
                                    )}
                                </div>
                                <ModalConsumer>
                                    {({ showModal }) => (
                                        <ActionButton
                                            variant="primary"
                                            size="lg"
                                            className={classes.confirmButton}
                                            disabled={isSend ? false : !formIsValid()}
                                            onClick={async () => {
                                                if (transactionType === TRANSACTION_TYPES.SEND_FIAT) {
                                                    onClose && onClose();
                                                    
                                                    // Pass only depositType to step 2 (currency/amount moved to step 2)
                                                    if (depositType === 'BANK') {
                                                        // Open the bank recipient form (existing dynamic form)
                                                        showModal(SendFiatRecipientDetailsDialog, {
                                                            initialValues: {
                                                                accountId,
                                                                customerId: customerId,
                                                                depositType: depositType,
                                                                date: currentValues.date
                                                            },
                                                            onComplete
                                                        });
                                                    } else {
                                                        // Open stablecoin recipient form
                                                        showModal(SendStablecoinRecipientDetailsDialog, {
                                                            initialValues: {
                                                                accountId,
                                                                customerId: customerId,
                                                                depositType: depositType,
                                                                date: currentValues.date
                                                            },
                                                            onComplete
                                                        });
                                                    }
                                                } else {
                                                    if (transactionCategory === 'Top up') {
                                                        // Create OTC pending deposit
                                                        setLoading(true);
                                                        try {
                                                            await client.mutate({
                                                                mutation: createOtcOrderAdminMutation,
                                                                variables: {
                                                                    input: {
                                                                        customerId: customerId,
                                                                        side: 'DEPOSIT',
                                                                        currency: (currentValues.currency || 'USD'),
                                                                        amount: parseFloat(currentValues.depositAmount),
                                                                        createdBy: user?.email,
                                                                        metadata: {
                                                                            transactionDate: currentValues.date || new Date().toISOString().substring(0,10)
                                                                        }
                                                                    }
                                                                }
                                                            });
                                                            setLoading(false);
                                                            onClose && onClose();
                                                            onComplete && onComplete(true);
                                                            try { toast('Top up created'); } catch(_e) { /* noop */ }
                                                        } catch (e) {
                                                            console.error(e);
                                                            setLoading(false);
                                                            onClose && onClose();
                                                            onComplete && onComplete(false);
                                                            try { toast('Failed to create top up'); } catch(_e) { /* noop */ }
                                                        }
                                                    } else {
                                                        setLoading(true);
                                                        setSubmitRequested(true);
                                                    }
                                                }
                                            }}
                                        >
                                            {isSend ? 'Continue' : (transactionCategory === 'Top up' ? 'Done' : `Create ${transactionCategory.toLowerCase()}`)}
                                        </ActionButton>
                                    )}
                                </ModalConsumer>
                            </div>
                        }
                    </CardContent>
                </Card>
            </Fade>
        </Modal>
    )
}

export default PendingTransactionDialog;